import React, { useEffect } from "react";
import { useI18n } from "../../i18n";
import { formatDateTime } from "../../utils/dates";
import { formatAmount, formatMoney, round2 } from "../utils/money";

const AUTO_CLOSE_MS = 5000;

/**
 * Sale confirmation. The change to hand back is the biggest thing on screen,
 * because that is the one number the cashier acts on immediately.
 *
 * When there is no change to count the overlay clears itself, so the next
 * customer can be served without a tap.
 */
const ReceiptOverlay = ({ receipt, storeName, currency, onNewSale }) => {
  const { t } = useI18n();
  const hasChange = receipt.changeDue > 0;
  const onCredit =
    receipt.paymentMethod === "credit"
      ? round2(Math.max(receipt.total - receipt.amountPaid, 0))
      : 0;

  const methodLabel =
    receipt.paymentMethod === "mobile_payment"
      ? t("common.payment.mobile_payment_long")
      : t(`common.payment.${receipt.paymentMethod}`);

  useEffect(() => {
    if (hasChange || onCredit > 0) return undefined;

    const timer = setTimeout(onNewSale, AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [hasChange, onCredit, onNewSale]);

  return (
    <div className="fixed inset-0 z-[70] bg-slate-950/95 backdrop-blur flex flex-col">
      <div className="flex-1 overflow-y-auto flex items-center justify-center p-5">
        <div className="w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400 flex items-center justify-center text-3xl">
              ✓
            </div>
            <h2 className="mt-3 text-xl font-bold text-white">
              {receipt.queued
                ? t("pos.receipt.savedOnDevice")
                : t("pos.receipt.completed")}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {receipt.queued
                ? t("pos.receipt.offlineNote")
                : receipt.receiptNumber
                  ? t("pos.receipt.receiptNo", { number: receipt.receiptNumber })
                  : t("pos.receipt.recorded")}
            </p>
          </div>

          {hasChange && (
            <div className="mt-5 rounded-3xl bg-amber-500 text-slate-900 p-5 text-center shadow-2xl">
              <p className="text-xs font-bold uppercase tracking-widest opacity-80">
                {t("pos.receipt.changeBack")}
              </p>
              <p className="text-5xl font-extrabold tabular-nums mt-1">
                {formatAmount(receipt.changeDue)}
              </p>
              <p className="text-sm font-bold opacity-80">{currency}</p>
            </div>
          )}

          {onCredit > 0 && (
            <div className="mt-5 rounded-3xl bg-amber-500 text-slate-900 p-5 text-center shadow-2xl">
              <p className="text-xs font-bold uppercase tracking-widest opacity-80">
                {t("pos.receipt.staysOnCredit")}
              </p>
              <p className="text-5xl font-extrabold tabular-nums mt-1">
                {formatAmount(onCredit)}
              </p>
              <p className="text-sm font-bold opacity-80">{currency}</p>
            </div>
          )}

          <div className="mt-4 rounded-2xl bg-slate-900 border border-slate-800 divide-y divide-slate-800">
            {receipt.lines.map((line) => (
              <div
                key={line.product.id}
                className="flex items-center justify-between gap-3 px-4 py-2.5"
              >
                <span className="text-sm text-slate-200 line-clamp-2">
                  <span className="text-slate-400 tabular-nums me-2">
                    {line.quantity}×
                  </span>
                  {line.product.name}
                </span>
                <span className="text-sm font-semibold text-slate-300 tabular-nums shrink-0">
                  {formatAmount(line.product.unit_price * line.quantity)}
                </span>
              </div>
            ))}

            {receipt.discountAmount > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-sm text-slate-400">
                  {t("pos.ticket.discount")}
                </span>
                <span className="text-sm font-semibold text-amber-400 tabular-nums">
                  −{formatAmount(receipt.discountAmount)}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm font-bold text-white">
                {t("pos.receipt.totalWith", { method: methodLabel })}
              </span>
              <span className="text-lg font-extrabold text-emerald-400 tabular-nums">
                {formatMoney(receipt.total, currency)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-slate-800 bg-slate-900/80">
        <div className="max-w-md mx-auto flex gap-3">
          <button
            type="button"
            onClick={() => window.print()}
            className="h-14 px-5 rounded-2xl bg-slate-800 border border-slate-700 text-slate-200 font-bold hover:bg-slate-700 active:scale-95 transition-all"
          >
            {t("pos.receipt.print")}
          </button>
          <button
            type="button"
            autoFocus
            onClick={onNewSale}
            className="flex-1 h-14 rounded-2xl bg-primary-600 hover:bg-primary-500 text-white font-extrabold text-lg shadow-xl shadow-primary-600/20 active:scale-[0.99] transition-all"
          >
            {t("pos.receipt.next")}
          </button>
        </div>
      </div>

      {/* Printed by the browser's print dialog; hidden on screen. */}
      <div id="pos-print-receipt" aria-hidden="true">
        <h1>{storeName}</h1>
        {receipt.receiptNumber && (
          <p>{t("pos.receipt.receiptNo", { number: receipt.receiptNumber })}</p>
        )}
        <p>{formatDateTime(receipt.soldAt)}</p>
        <table>
          <tbody>
            {receipt.lines.map((line) => (
              <tr key={line.product.id}>
                <td>
                  {line.quantity}× {line.product.name}
                </td>
                <td className="pos-print-amount">
                  {formatAmount(line.product.unit_price * line.quantity)}
                </td>
              </tr>
            ))}
            {receipt.discountAmount > 0 && (
              <tr>
                <td>{t("pos.ticket.discount")}</td>
                <td className="pos-print-amount">
                  −{formatAmount(receipt.discountAmount)}
                </td>
              </tr>
            )}
            <tr className="pos-print-total">
              <td>{t("pos.receipt.printTotal")}</td>
              <td className="pos-print-amount">
                {formatMoney(receipt.total, currency)}
              </td>
            </tr>
            <tr>
              <td>{methodLabel}</td>
              <td className="pos-print-amount">
                {formatAmount(receipt.amountPaid)}
              </td>
            </tr>
            {hasChange && (
              <tr>
                <td>{t("pos.receipt.printChange")}</td>
                <td className="pos-print-amount">
                  {formatAmount(receipt.changeDue)}
                </td>
              </tr>
            )}
            {onCredit > 0 && (
              <tr>
                <td>{t("pos.receipt.printOnCredit")}</td>
                <td className="pos-print-amount">{formatAmount(onCredit)}</td>
              </tr>
            )}
          </tbody>
        </table>
        <p className="pos-print-footer">{t("pos.receipt.thanks")}</p>
      </div>
    </div>
  );
};

export default ReceiptOverlay;
